import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { defaultCategoryMappings, CategoryMapping } from '../data/categoryMapping';
import { getProductCategories } from '../lib/supabase';
import { EmptyState, PageBar, PrimaryButton, SearchInput, Segmented } from '../components/admin/dash/Toolbar';
import { StatRow } from '../components/admin/dash/StatCard';
import { CategoryRow } from '../components/admin/categories/CategoryRow';

interface ProductCount {
  name: string;
  productCount: number;
}

type ActiveFilter = 'active' | 'all';

// Categories: read-only browse over the static defaultCategoryMappings, with
// product counts joined in from Supabase. Edits (display name, group, active,
// sort) are local-only — there is no persistence endpoint yet, per CLAUDE.md.
const AdminCategories: React.FC = () => {
  const [categories, setCategories] = useState<CategoryMapping[]>(defaultCategoryMappings);
  const [productCounts, setProductCounts] = useState<ProductCount[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const q = searchParams.get('q') ?? '';
  const group = searchParams.get('group') ?? 'all';
  const activeFilter: ActiveFilter = searchParams.get('active') === 'all' ? 'all' : 'active';

  useEffect(() => {
    getProductCategories()
      .then(setProductCounts)
      .catch((error) => console.error('Error fetching product counts:', error));
  }, []);

  const setParam = (key: string, value: string) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (!value || value === 'all') next.delete(key);
        else next.set(key, value);
        return next;
      },
      { replace: true }
    );
  };

  const groups = useMemo(() => ['all', ...Array.from(new Set(categories.map((cat) => cat.group)))], [categories]);

  const getProductCount = (categoryName: string): number => productCounts.find((p) => p.name === categoryName)?.productCount ?? 0;

  const maxCount = useMemo(() => Math.max(1, ...categories.map((cat) => getProductCount(cat.name))), [categories, productCounts]);

  const filteredCategories = useMemo(
    () =>
      categories
        .filter((cat) => cat.name.toLowerCase().includes(q.toLowerCase()) || cat.displayName.toLowerCase().includes(q.toLowerCase()))
        .filter((cat) => group === 'all' || cat.group === group)
        .filter((cat) => activeFilter === 'all' || cat.isActive)
        .sort((a, b) => a.sortOrder - b.sortOrder),
    [categories, q, group, activeFilter]
  );

  const updateCategory = (id: string, updates: Partial<CategoryMapping>) => {
    setCategories((prev) => prev.map((cat) => (cat.id === id ? { ...cat, ...updates } : cat)));
  };

  const toggleActive = (id: string) => {
    const category = categories.find((cat) => cat.id === id);
    if (category) updateCategory(id, { isActive: !category.isActive });
  };

  const saveChanges = () => {
    toast.info('Not saved yet', { description: 'Categories are read-only for now — changes here only last for this browser session.' });
  };

  return (
    <div className="flex h-full flex-col font-poppins text-[color:var(--oa-text)] antialiased">
      <PageBar
        title="Categories"
        meta={`${filteredCategories.length} of ${categories.length}`}
        actions={<PrimaryButton onClick={saveChanges}>Save all changes</PrimaryButton>}
      >
        <div className="flex flex-1 items-center gap-2">
          <SearchInput value={q} onChange={(v) => setParam('q', v)} placeholder="Search categories" />
          <select
            value={group}
            onChange={(e) => setParam('group', e.target.value)}
            className="h-8 rounded-md border border-[color:var(--oa-line-2)] bg-[color:var(--oa-raised)] px-2 text-[12.5px] text-[color:var(--oa-text)] outline-none focus:border-[color:var(--oa-accent)]"
          >
            {groups.map((g) => (
              <option key={g} value={g}>
                {g === 'all' ? 'All groups' : g}
              </option>
            ))}
          </select>
          <Segmented
            value={activeFilter}
            onChange={(v) => setParam('active', v)}
            options={[
              { value: 'active', label: 'Active' },
              { value: 'all', label: 'All' },
            ]}
          />
        </div>
      </PageBar>

      <div className="min-h-0 flex-1 overflow-auto">
        <div className="mx-auto max-w-5xl px-4 py-4">
          <StatRow
            className="mb-4"
            stats={[
              { label: 'Total categories', value: categories.length, caption: `Across ${groups.filter((g) => g !== 'all').length} groups` },
              {
                label: 'Active',
                value: categories.filter((cat) => cat.isActive).length,
                caption: `${Math.round((categories.filter((cat) => cat.isActive).length / Math.max(1, categories.length)) * 100)}% shown on the site`,
              },
              {
                label: 'With products',
                value: categories.filter((cat) => getProductCount(cat.name) > 0).length,
                caption: `${categories.filter((cat) => getProductCount(cat.name) === 0).length} have no live products`,
              },
            ]}
          />

          <div className="oa-cat-row gap-3 px-2 pb-2 text-[11px] font-semibold uppercase tracking-[0.05em] text-[color:var(--oa-faint)]">
            <span />
            <span>Category</span>
            <span className="oa-hide-phone">Group</span>
            <span>Products</span>
            <span>Status</span>
          </div>

          {filteredCategories.length === 0 ? (
            <EmptyState title="No categories match your filters" hint="Try clearing the search or picking a different group." />
          ) : (
            <div className="space-y-0.5">
              {filteredCategories.map((category, index) => (
                <CategoryRow
                  key={category.id}
                  category={category}
                  index={index}
                  productCount={getProductCount(category.name)}
                  maxCount={maxCount}
                  groups={groups.filter((g) => g !== 'all')}
                  editing={editingId === category.id}
                  onStartEdit={() => setEditingId(category.id)}
                  onStopEdit={() => setEditingId(null)}
                  onUpdate={(updates) => updateCategory(category.id, updates)}
                  onToggleActive={() => toggleActive(category.id)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCategories;
